<template>
  <q-card-section class="auth-error-panel column col items-center gap-lg q-pt-xl q-px-xl q-pb-md">
    <div class="onboarding-icon-badge row items-center justify-center">
      <lucide-icon
        :name="screenCopy.icon"
        size="40px"
        :stroke-width="2"
        aria-hidden="true"
      />
    </div>

    <div
      class="column items-center gap-xxl text-center"
      role="status"
      aria-live="polite"
    >
      <h1 class="text-28 text-font-semibold">
        {{ screenCopy.headline }}
        <span
          v-if="screenCopy.headlineAccent"
          class="text-nowrap text-brand"
        >
          {{ screenCopy.headlineAccent }}
        </span>
      </h1>

      <div
        v-if="screenCopy.subline?.length"
        class="onboarding-subline column items-center gap-xs text-16 text-primary"
      >
        <p
          v-for="(line, index) in screenCopy.subline"
          :key="index"
          class="q-ma-none"
        >
          {{ line }}
        </p>
      </div>
    </div>

    <div class="column col-grow items-center justify-end gap-xs full-width">
      <q-btn
        flat
        no-caps
        color="secondary"
        class="onboarding-action-btn sm-radius text-14"
        @click="onUseDifferentAccount"
      >
        {{ screenCopy.secondaryActionLabel }}
      </q-btn>

      <q-btn
        v-if="screenCopy.mainActionLabel"
        unelevated
        no-caps
        color="brand"
        class="onboarding-action-btn sm-radius text-14 text-font-semibold"
        @click="onMainAction"
      >
        {{ screenCopy.mainActionLabel }}
        <lucide-icon
          v-if="screenCopy.mainActionIcon"
          :name="screenCopy.mainActionIcon"
          size="18px"
          color="white"
          class="q-ml-xs"
          aria-hidden="true"
        />
      </q-btn>
    </div>
  </q-card-section>
</template>

<script setup>
  import { computed } from 'vue'

  import { useAccountStore } from 'src/stores/account'
  import { useAuthStore } from 'src/stores/auth'

  import { useSupportModal } from 'src/composables/app/mutation/useSubmitSupportMutation'

  import { AUTH_MAIN_ACTION } from 'src/services/consts/app.const'
  import {
    hasAccountLoadError,
    isTrialExpiredByDate,
    resolveAuthErrorCopy,
  } from 'src/services/helpers/auth.helper'

  // SessionGate has already decided the session failed — this panel only
  // classifies which failure and offers the way out of it.

  const accountStore = useAccountStore()
  const authStore = useAuthStore()
  const { openSupportModal } = useSupportModal()

  const MAIN_ACTIONS = {
    [AUTH_MAIN_ACTION.RETRY_BOOTSTRAP]: () => authStore.retryBootstrap(),
    [AUTH_MAIN_ACTION.LOGIN]: () => authStore.login(),
    [AUTH_MAIN_ACTION.CONTACT_SUPPORT]: () => openSupportModal(),
  }

  const screenCopy = computed(() =>
    resolveAuthErrorCopy({
      authError: authStore.error,
      accountError: accountStore.error,
      accountData: accountStore.accountData,
      isAuthenticated: authStore.isAuthenticated,
    })
  )

  function onMainAction() {
    MAIN_ACTIONS[screenCopy.value.mainAction]?.()
  }

  // A failed Auth0 callback or an unusable session must skip the backend logout
  // call — there is no valid session for it to invalidate.
  function onUseDifferentAccount() {
    const authError =
      (!!authStore.error && !authStore.isAuthenticated) ||
      hasAccountLoadError({
        isAuthenticated: authStore.isAuthenticated,
        accountError: accountStore.error,
        accountData: accountStore.accountData,
      }) ||
      isTrialExpiredByDate(accountStore.accountData ?? {})

    authStore.logout({ authError: authError })
  }
</script>

<style lang="scss">
  // Stacks above AuthShell's decorative .floating-icons (z-index: 1), and min-height
  // keeps the longest reason (email_not_verified) from overflowing the flex column.
  .auth-error-panel {
    position: relative;
    z-index: 2;
    min-height: 0;
  }
</style>
